import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { ILoginResponse } from "@app/auth/models/iLoginResponse.model";
import { SpinnerService } from "@app/shared/services/spinner/spinner.service";
import {
  BehaviorSubject, EMPTY, Observable, throwError
} from "rxjs";
import {
  catchError, filter, finalize, switchMap, take
} from "rxjs/operators";

import { AuthService } from "../authentication/auth.service";

@Injectable()
export class Interceptor implements HttpInterceptor {
  private requestCounter = 0;
  private isRefreshingToken = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  private longestRequestEndTime: number = 0;

  constructor(
    private authService: AuthService,
    private router: Router,
    private spinner: SpinnerService
  ) {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        if (this.requestCounter === 0) {
          this.longestRequestEndTime = 0;
          this.spinner.hide();
        }
      });
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const startTime = Date.now();
    this.requestCounter += 1;
    this.spinner.show();

    if (this.isLoginRequest(req) || this.isResetPasswordRequest(req)) {
      return this.handleRequestWithoutSpinner(next, req, startTime);
    }

    if (!this.authService.isLoggedIn()) {
      this.redirectToLogin();
      return this.handleRequestWithoutSpinner(next, req, startTime);
    }

    const authReq = this.addAuthHeader(req);

    return next.handle(authReq).pipe(
      catchError((err) => this.handleError(err, req, next)),

      finalize(() => this.decrementRequestCounter(startTime))
    );
  }

  private decrementRequestCounter(startTime: number): void {
    const endTime = Date.now();
    this.requestCounter -= 1;

    if (endTime - startTime > this.longestRequestEndTime) {
      this.longestRequestEndTime = endTime - startTime;
    }

    if (this.requestCounter <= 0) {
      setTimeout(() => {
        if (endTime - startTime >= this.longestRequestEndTime) {
          this.longestRequestEndTime = 0;
        }
      }, 0);

      this.spinner.hide();
    }
  }

  private isLoginRequest(req: HttpRequest<any>): boolean {
    return req.url.includes("/signin");
  }

  private isResetPasswordRequest(req: HttpRequest<any>): boolean {
    return req.url.includes("/password/reset");
  }

  private handleRequestWithoutSpinner(
    next: HttpHandler,
    req: HttpRequest<any>,
    startTime: number
  ): Observable<HttpEvent<any>> {
    return next
      .handle(req)
      .pipe(finalize(() => this.decrementRequestCounter(startTime)));
  }

  private redirectToLogin(): void {
    this.router.navigate(["/login"]);
  }

  private addAuthHeader(req: HttpRequest<any>): HttpRequest<any> {
    const accessToken = this.authService.getAccessToken;
    return req.clone({
      setHeaders: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }

  private handleError(
    err: any,
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (err.status === 401) {
      return this.handle401Error(err, req, next);
    }

    return throwError(() => err);
  }

  private handle401Error(
    err: any,
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    console.log(err);
    if (err.error.detail === "Given token not valid for any token type") {
      return this.refreshTokenAndRetry(req, next);
    }

    if (err.error.code === "token_not_valid") {
      this.handleInvalidToken();
      return EMPTY;
    }

    return this.refreshTokenAndRetry(req, next);
  }

  private handleInvalidToken(): void {
    localStorage.clear();
    this.redirectToLogin();
    this.requestCounter = 0;
    this.spinner.hide();
  }

  private refreshTokenAndRetry(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (this.isRefreshingToken) {
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap(() => next.handle(this.addAuthHeader(req)))
      );
    }
    this.isRefreshingToken = true;
    this.refreshTokenSubject.next(null);

    return this.authService.refreshToken().pipe(
      switchMap((response: ILoginResponse) => {
        this.isRefreshingToken = false;
        this.storeNewTokens(response);
        this.refreshTokenSubject.next(response.accessToken);
        return next.handle(this.addAuthHeader(req));
      }),
      catchError((err) => {
        this.isRefreshingToken = false;
        this.handleInvalidToken();
        return throwError(() => err);
      }),
      finalize(() => this.decrementRequestCounter(Date.now()))
    );
  }

  private storeNewTokens(response: ILoginResponse): void {
    localStorage.setItem("access_token", response.accessToken);
    localStorage.setItem("refresh_token", response.refreshToken);
  }
}
